import { Link } from 'react-router-dom';
import { LEGAL_DOCS, legalDoc } from '@/content/legal';
import { formatDate } from '@/lib/format';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/Controls';
import { ButtonLink } from '@/components/Button';
import { Prose } from '@/components/Prose';
import { Section } from '@/components/Section';
import { Seo } from '@/components/Seo';

/** 隐私政策与使用条款共用一个版式，只有内容不同。 */
export function LegalPage({ slug }: { slug: 'privacy' | 'terms' }) {
  const doc = legalDoc(slug);

  if (!doc) {
    return (
      <>
        <Seo path="/" title="页面不存在 — Havorix™" description="请求的页面不存在。" />
        <Section spacing="loose">
          <EmptyState
            title="未找到该文档"
            text="链接可能有误。隐私政策与使用条款均可在页脚找到。"
            actions={<ButtonLink to="/" variant="primary">返回首页</ButtonLink>}
          />
        </Section>
      </>
    );
  }

  const path = `/${doc.slug}`;
  const other = LEGAL_DOCS.find((item) => item.slug !== doc.slug);

  return (
    <>
      <Seo
        path={path}
        title={`${doc.title} — Havorix™`}
        description={doc.intro}
        crumbs={[
          { name: '首页', path: '/' },
          { name: doc.title, path },
        ]}
      />

      <Section tone="grid" spacing="tight" className="border-b border-line">
        <div className="max-w-3xl">
          <span className="label-mono">法务</span>
          <h1 className="mt-3 text-3xl font-semibold leading-tight text-fg md:text-[2.25rem]">{doc.title}</h1>
          <p className="mt-4 text-base leading-relaxed text-fg-muted">{doc.intro}</p>
          <p className="metric-value mt-4 text-xs text-fg-subtle">最近更新：{formatDate(doc.updated)}</p>
        </div>
      </Section>

      <Section>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start">
          <nav aria-label="条款目录" className="lg:sticky lg:top-24">
            <span className="label-mono">目录</span>
            <ol className="mt-3 space-y-2">
              {doc.sections.map((section, index) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="flex gap-2.5 text-sm leading-relaxed text-fg-muted transition-colors hover:text-fg"
                  >
                    <span className="metric-value text-xs text-fg-subtle">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span>{section.title}</span>
                  </a>
                </li>
              ))}
            </ol>

            {other ? (
              <Card className="mt-6 flex flex-col gap-2">
                <span className="label-mono">另见</span>
                <Link to={`/${other.slug}`} className="text-sm font-medium text-fg hover:text-accent">
                  {other.title}
                </Link>
              </Card>
            ) : null}
          </nav>

          <div className="flex flex-col gap-10">
            {doc.sections.map((section) => (
              <section key={section.id} id={section.id}>
                <h2 className="text-xl font-semibold text-fg">{section.title}</h2>
                <Prose blocks={section.blocks} className="mt-4" />
              </section>
            ))}

            <p className="border-t border-line pt-6 text-xs leading-relaxed text-fg-subtle">
              这份文档会随平台行为变化而更新；如对某一条有疑问，写信到 contact@havorix.cn 即可。
            </p>
          </div>
        </div>
      </Section>
    </>
  );
}
